<script>
import {useUserStore} from "../stores/user";
import {useI18n} from "vue-i18n";
import {useRoute, useRouter} from "vue-router";
import {useOrganizationStore} from "../stores/organizations";
import LoadingSpinner from "./components/loadingSpinner.vue";
import Icon from "./components/Icon.vue";
import BaseModal from "./components/BaseModal.vue";

/**
 * This is the intermediate route when the user first logs in
 * It checks for permissions before routing to the appropriate dashboard (client, donor dash)
 * It also checks for TOS, etc and forces the user to agree before proceeding any further
 */

export default {
  name: 'RouteGuard',
  components: {Icon, LoadingSpinner, BaseModal},

  setup() {
    // I18n
    const {t} = useI18n();

    // Router
    const router = useRouter();
    const route = useRoute();

    const userStore = useUserStore();
    const organizationStore = useOrganizationStore();

    return {
      router,
      route,
      userStore,
      organizationStore
    };
  },
  data() {
    return {
      isLoading: true,
      showTosModal: false,
      processingTerms: false,
      tosClicked: false,
      showDocumentModal: false,
      currentDocumentTitle: '',
      currentDocumentUrl: '',
      loadingDocument: false
    };
  },

  computed: {
    agreeButtonEnabled() {
      return this.tosClicked;
    }
  },
  mounted() {
    this.initializeUser();
  },
  methods: {
    initializeUser() {
      try {
        this.userStore.userId = document.body.dataset.currentUserId;
        this.userStore.loadData(this.$route.params.org).then((res) => {
          if (res) {
            // Check Terms of Service
            if (!this.userStore.userAgreedToAllTerms) {
              this.isLoading = false;
              this.showTosModal = true;

            } else {
              // If terms accepted, proceed with organization check
              this.handleOrganizationRouting();
            }

          } else {
            this.logout();
          }
        });

      } catch (error) {
        console.error('Error initializing user:', error);
        this.logout();
      }
    },

    async handleOrganizationRouting() {
      const urlParams = new URLSearchParams(window.location.search);
      const returnTo = urlParams.get('return_to'); //Landing page populates 'return_to'

      //Is user coming from Landing page?
      if(returnTo) {
        try {
          const returnUrl = new URL(decodeURIComponent(returnTo));
          const route = this.$router.resolve(returnUrl.pathname);

          if(route.name === 'landing'){
            window.location.href = returnTo;
            return;
          }
        } catch (e) {
          console.error('Invalid return_to URL:', e);
        }
      }

      if (this.userStore.organizations.length === 0) {
        // User has no organization permissions
        if (this.userStore.userIsAdmin) {
          this.userStore.goToAdmin(false);
        }else {
          await this.userStore.getDonorOrgs(this.$route.params.org);
          if (this.userStore.currentOrganization) {
            // Redirect to donor dashboard
            this.$router.push({
              name: "donor-dashboard",
              params: {
                panel: 'profile',
                org: this.userStore.currentOrganization.slug
              }
            });
          }else{
            this.isLoading = false;
            this.userStore.signOut().then(() => {
              window.location.href = '/users/sign_in?reason=no_donations';
            });
          }
        }
      } else {
        // User has organization access
        // Route to client dashboard
        this.$router.replace({
          name: "client-dashboard"
        });
      }
    },

    acceptTerms() {
      if (this.processingTerms || !this.agreeButtonEnabled) return;

      this.processingTerms = true;

      // User agreed to terms, update user and proceed
      this.userStore.acceptTermsOfService().then(() => {
        this.showTosModal = false;
        this.isLoading = true;

        // Continue with organization routing
        this.handleOrganizationRouting();
      }).catch(() => {
        alert('Failed to accept terms. Please try again.');
      }).finally(() => {
        this.processingTerms = false;
      });
    },

    // Return user to sign in screen when TOS is declined
    declineTerms() {
      if (this.processingTerms) return;
      this.processingTerms = true;

      try {
        this.logout();
      } catch (error) {
        console.error('Error during logout:', error);
        this.logout();
      }
    },

    openDocument(type, title) {
      this.currentDocumentTitle = title;
      this.currentDocumentUrl = `/${type}`;
      this.showDocumentModal = true;

      // Mark as clicked
      if (type === 'tos') {
        this.tosClicked = true;
      }
    },

    onTosClicked() {
      this.openDocument('tos', 'Terms of Service');
    },

    closeDocumentModal() {
      this.showDocumentModal = false;
      this.currentDocumentUrl = '';
      this.currentDocumentTitle = '';
    },

    logout() {
      this.userStore.signOut();
    },

  }

};
</script>


<template>
  <div class="route-guard-container">
    <!-- Loading state -->
    <div
      v-if="isLoading"
      class="loading-wrapper"
    >
      <div class="spinner">
        <loading-spinner size="small" />
      </div>
      <p class="text-gray-600 mt-4">
        Signing in...
      </p>
    </div>

    <!-- Terms & Conditions Page -->
    <div
      v-if="showTosModal"
      class="terms-page"
    >
      <div class="terms-container">
        <div class="terms-header">
          <h2 class="text-2xl font-semibold text-gray-900">
            Terms and Conditions
          </h2>
          <p class="text-gray-600 mt-2">
            To continue using this application, you must review and accept our updated terms
            and conditions.
          </p>
        </div>

        <div class="terms-body">
          <div class="terms-list">
            <div
              class="terms-item"
              @click="onTosClicked"
            >
              <div class="checkbox-container">
                <input
                  type="checkbox"
                  :checked="tosClicked"
                  class="terms-checkbox"
                  @click="onTosClicked"
                >
              </div>
              <div class="terms-text">
                <span class="terms-link">Terms of Service</span>
              </div>
            </div>
          </div>
        </div>

        <div class="terms-footer">
          <button
            :disabled="processingTerms"
            class="btn-decline"
            @click="declineTerms"
          >
            Decline & Logout
          </button>
          <button
            :disabled="processingTerms"
            class="btn-accept"
            :class="{'disabled': !agreeButtonEnabled}"
            @click="acceptTerms"
          >
            <span
              v-if="processingTerms"
              class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"
            />
            I Agree
          </button>
        </div>
      </div>
    </div>

    <!-- Document Modal using BaseModal -->
    <base-modal
      v-if="showDocumentModal"
      :show="showDocumentModal"
      :title="currentDocumentTitle"
      size="large"
      @close="closeDocumentModal"
    >
      <div class="pdf-container">
        <iframe
          :src="currentDocumentUrl"
          class="pdf-iframe"
          frameborder="0"
          width="100%"
          height="600px"
        />
      </div>
    </base-modal>
  </div>
</template>


<style scoped lang="scss">
.route-guard-container {
  @apply fixed inset-0 z-50;
}

.terms-item {
  @apply flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-100 rounded transition-colors;
}

.checkbox-container {
  @apply flex-shrink-0;
}

.terms-checkbox {
  @apply w-5 h-5 rounded border-2 border-gray-300 focus:ring-blue-500;
  accent-color: #2563eb;
  color: #2563eb;
}

.terms-text {
  @apply flex-grow;
}

.terms-link {
  @apply text-sm text-blue-600 cursor-pointer underline;

  &:hover {
    @apply text-blue-800;
  }
}

.loading-wrapper {
  @apply flex flex-col justify-center items-center h-full bg-gray-50;
}

// Terms Page Styles
.terms-page {
  @apply fixed inset-0 bg-gray-50 flex items-center justify-center z-50;
}

.terms-container {
  @apply bg-white rounded-lg w-11/12 max-w-2xl shadow-2xl overflow-hidden;
}

.terms-header {
  @apply px-6 py-6 border-b border-gray-200;
}

.terms-body {
  @apply p-6;
}

.terms-list {
  @apply bg-gray-50 p-4 rounded border border-gray-200 space-y-2;
}

.terms-footer {
  @apply px-6 py-4 flex justify-end gap-3 border-t border-gray-200;
}

.btn-decline {
  @apply px-6 py-2 bg-gray-500 text-white rounded font-medium transition-colors duration-200 flex items-center;
}

.btn-decline:hover:not(:disabled) {
  @apply bg-gray-600;
}

.btn-accept {
  @apply px-6 py-2 bg-default_primary_dark text-white rounded font-medium transition-colors duration-200 flex items-center;

  &.disabled {
    @apply bg-gray-400 cursor-not-allowed;
  }
}

.btn-decline:disabled,
.btn-accept:disabled {
  @apply opacity-60 cursor-not-allowed;
}

// PDF Modal Styles
.pdf-container {
  @apply w-full;
}

.pdf-iframe {
  @apply w-full border-0 rounded;
  min-height: 600px;
}
</style>
